import {
  Grid,
  Box,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
} from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import GenericNumberField from "../../components/GenericNumberField";
import GenericYearMonthPicker from "../../components/GenericYearMonthPicker";
import dayjs from "dayjs";

const ImpostosFormSchema = z
  .object({
    idImposto: z.number().readonly(),
    referencia: z.string().min(1, {
      message: "Mês de referência é obrigatório",
    }),
    referenciaInicio: z.string().min(1, {
      message: "Mês de inicio é obrigatório",
    }),
    referenciaFim: z.string().min(1, {
      message: "Mês de fim é obrigatório",
    }),
    terceiros: z.number({
      required_error: "Valor de terceiros é obrigatório",
    }),
    rat: z.number({
      required_error: "Valor de RAT é obrigatório",
    }),
    inss: z.number({
      required_error: "Valor de parte emp. é obrigatório",
    }),
  })
  .partial({
    idImposto: true,
    referencia: true,
    referenciaInicio: true,
    referenciaFim: true,
  })
  .required({
    terceiros: true,
    rat: true,
    inss: true,
  })
  .refine(
    (data) => {
      if (data.idImposto) {
        return !!data.referencia && data.referencia !== "Invalid Date";
      }
      return true;
    },
    {
      message: "Data de referência inválida!",
      path: ["referencia"],
    }
  )
  .refine(
    (data) => {
      if (!data.idImposto) {
        return (
          !!data.referenciaInicio && data.referenciaInicio !== "Invalid Date"
        );
      }
      return true;
    },
    {
      message: "Data de inicio da referência inválida!",
      path: ["referenciaInicio"],
    }
  )
  .refine(
    (data) => {
      if (!data.idImposto) {
        return !!data.referenciaFim && data.referenciaFim !== "Invalid Date";
      }
      return true;
    },
    {
      message: "Data de fim da referência inválida!",
      path: ["referenciaFim"],
    }
  );

type ImpostosFormData = z.infer<typeof ImpostosFormSchema>;

type ImpostosFormProps = {
  impostosValues: ImpostosFormData;
  onSubmit(data: ImpostosFormData): void;
  onClose(event: any): void;
};

export default function ImpostosForm({
  impostosValues,
  onSubmit,
  onClose,
}: ImpostosFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ImpostosFormData>({
    mode: "onChange",
    resolver: zodResolver(ImpostosFormSchema),
    defaultValues: {
      idImposto: impostosValues.idImposto || undefined,
      referencia: impostosValues.referencia || undefined,
      referenciaInicio: impostosValues.referenciaInicio || undefined,
      referenciaFim: impostosValues.referenciaFim || undefined,
      terceiros: +impostosValues.terceiros || undefined,
      rat: +impostosValues.rat || undefined,
      inss: +impostosValues.inss || undefined,
    },
  });

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const handleReferenciaChange = (event: Date) => {
    setValue("referencia", event ? dayjs(event).format("YYYY-MM") : "", {
      shouldValidate: true,
    });
  };

  const handleReferenciaInicioChange = (event: Date) => {
    setValue("referenciaInicio", event ? dayjs(event).format("YYYY-MM") : "", {
      shouldValidate: true,
    });
  };

  const handleReferenciaFimChange = (event: Date) => {
    setValue("referenciaFim", event ? dayjs(event).format("YYYY-MM") : "", {
      shouldValidate: true,
    });
  };

  const submit = (data: ImpostosFormData) => {
    if (data.idImposto) {
      const body: any = {
        impostos: {
          referencia: data.referencia,
          terceiros: data.terceiros,
          rat: data.rat,
          inss: data.inss,
        },
      };
      onSubmit(body);
    } else {
      const body: any = {
        impostos: {
          referenciaInicio: data.referenciaInicio,
          referenciaFim: data.referenciaFim,
          terceiros: data.terceiros,
          rat: data.rat,
          inss: data.inss,
        },
      };
      onSubmit(body);
    }
  };

  const handleTerceirosChange = (event: any) => {
    const adjustedValorValue: number = event ? +event : event;
    setValue("terceiros", adjustedValorValue, { shouldValidate: true });
  };

  const handleRatChange = (event: any) => {
    const adjustedValorValue: number = event ? +event : event;
    setValue("rat", adjustedValorValue, { shouldValidate: true });
  };

  const handleINSSChange = (event: any) => {
    const adjustedValorValue: number = event ? +event : event;
    setValue("inss", adjustedValorValue, { shouldValidate: true });
  };

  return (
    <form
      onSubmit={handleSubmit(submit)}
      onClick={() => {
        console.log(errors);
      }}
      className={styles.formDiv}
    >
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Controller
                name="idImposto"
                control={control}
                defaultValue={impostosValues.idImposto}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    disabled
                    label="ID"
                    error={Boolean(errors.idImposto)}
                    helperText={errors.idImposto?.message}
                    placeholder={"Novo imposto"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            {impostosValues.idImposto ? (
              <Grid item xs={4}>
                <Controller
                  name="referencia"
                  control={control}
                  defaultValue={impostosValues.referencia}
                  render={({ field: { ref, onChange, ...field } }) => (
                    <GenericYearMonthPicker
                      required
                      label="Referência"
                      inputRef={ref}
                      placeholder="Digite o mês de referência"
                      onChange={handleReferenciaChange}
                      error={Boolean(errors.referencia)}
                      helperText={errors.referencia?.message}
                      {...field}
                    />
                  )}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={2}>
                  <Controller
                    name="referenciaInicio"
                    control={control}
                    defaultValue={impostosValues.referenciaInicio}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <GenericYearMonthPicker
                        required
                        label="Inicio"
                        inputRef={ref}
                        placeholder="Digite o mês de inicio da referência"
                        onChange={handleReferenciaInicioChange}
                        error={Boolean(errors.referenciaInicio)}
                        helperText={errors.referenciaInicio?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Controller
                    name="referenciaFim"
                    control={control}
                    defaultValue={impostosValues.referenciaFim}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <GenericYearMonthPicker
                        required
                        label="Fim"
                        inputRef={ref}
                        placeholder="Digite o mês de fim da referência"
                        onChange={handleReferenciaFimChange}
                        error={Boolean(errors.referenciaFim)}
                        helperText={errors.referenciaFim?.message}
                        {...field}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <Controller
                name="terceiros"
                control={control}
                defaultValue={impostosValues.terceiros}
                render={({ field: { ref, onChange, ...field } }) => (
                  <GenericNumberField
                    required
                    label="Terceiros"
                    placeholder="Digite o valor de terceiros"
                    error={Boolean(errors.terceiros)}
                    helperText={errors.terceiros?.message}
                    inputRef={ref}
                    decimals={5}
                    onChange={(data: any) => {
                      handleTerceirosChange(data);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="rat"
                control={control}
                defaultValue={impostosValues.rat}
                render={({ field: { ref, onChange, ...field } }) => (
                  <GenericNumberField
                    required
                    label="RAT"
                    placeholder="Digite o valor de RAT"
                    error={Boolean(errors.rat)}
                    helperText={errors.rat?.message}
                    inputRef={ref}
                    decimals={5}
                    onChange={(data: any) => {
                      handleRatChange(data);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="inss"
                control={control}
                defaultValue={impostosValues.inss}
                render={({ field: { ref, onChange, ...field } }) => (
                  <GenericNumberField
                    required
                    label="INSS"
                    placeholder="Digite o valor de INSS"
                    error={Boolean(errors.inss)}
                    helperText={errors.inss?.message}
                    inputRef={ref}
                    decimals={5}
                    onChange={(data: any) => {
                      handleINSSChange(data);
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
